import { useCallback, useEffect, useRef, useState } from 'react';
import { useSwipeNavigation } from '../hooks/useSwipeNavigation';
import { OfflineLessonButton } from '../../offline/OfflineLessonButton';
import { Sparkles, MessageCircleQuestion, Zap, CheckCircle2, PanelLeftClose, PanelLeft, GraduationCap, NotebookText, ListOrdered, X, Users } from 'lucide-react';
import { useShellTitle } from '../../../components/layout/ShellTitleContext';
import { useAuthStore } from '../../../stores/authStore';
import Spinner from '../../../components/ui/Spinner';
import { useLectureStore } from '../store/useLectureStore';
import {
  getLesson,
  getLessonAudio,
  getProgress,
  putProgress,
  completeLesson,
  getProgressSummary,
} from '../services/lectureApi';
import type { WidgetRef } from '../services/lectureApi';
import { isLessonFullyEngaged, MIN_SLIDE_DWELL_MS } from '../lib/lessonCompletion';
import SlideStage from './SlideStage';
import AudioController from './AudioController';
import SlideProgressRail from './SlideProgressRail';
import AskQuestionPanel from './AskQuestionPanel';
import TutorPanel from './TutorPanel';
import { useFeature } from '../../../stores/featureStore';
import { useUiStore } from '../../../stores/uiStore';
import { useClassroomStore } from '../../classroom/store';
import { ClassPanel } from '../../classroom';

interface LecturePlayerProps {
  lessonId: string;
}

// Top-level container. Loads the lesson + audio manifest, hydrates the store,
// restores the resume point from /progress, and lays out the rail, slide stage,
// transport, and the (mutually exclusive) Explain / Ask side panels.
export default function LecturePlayer({ lessonId }: LecturePlayerProps) {
  const resetForLesson = useLectureStore((s) => s.resetForLesson);
  const loadLesson = useLectureStore((s) => s.loadLesson);
  const setLoadError = useLectureStore((s) => s.setLoadError);
  const isLoaded = useLectureStore((s) => s.isLoaded);
  const loadError = useLectureStore((s) => s.loadError);
  const lesson = useLectureStore((s) => s.lesson);
  const slides = useLectureStore((s) => s.slides);
  const currentSlideIndex = useLectureStore((s) => s.currentSlideIndex);
  const goToSlide = useLectureStore((s) => s.goToSlide);
  const markSlideCovered = useLectureStore((s) => s.markSlideCovered);
  const markComplete = useLectureStore((s) => s.markComplete);
  const completedSlides = useLectureStore((s) => s.completedSlides);
  // The store's own `xp` is a client-only counter (+5 per slide covered)
  // that never reached the server and reset on every lesson switch — it is
  // what the instructor saw as a badge that "doesn't work". The header now
  // shows `totalXp`, the real course-wide total from /progress/summary.
  const panel = useLectureStore((s) => s.panel);
  const setPanel = useLectureStore((s) => s.setPanel);
  const notesVisible = useLectureStore((s) => s.notesVisible);
  const setNotesVisible = useLectureStore((s) => s.setNotesVisible);

  // The Notes (narration transcript) toggle is presenter-only: a student
  // watching a presentation must never see the transcript, so the control —
  // and the transcript section it governs — is gated behind admin/instructor.
  const isAdmin = useAuthStore((s) => !!(s.user?.is_admin || s.user?.is_super_admin));

  // Mirror the admin flag into the lecture store. Advance-when-covered gating
  // is enforced inside the store's navigation actions (see lib/slideLock), and
  // a zustand action cannot subscribe to the auth store — so the component
  // that already knows hands it over.
  const setIsAdmin = useLectureStore((s) => s.setIsAdmin);
  useEffect(() => { setIsAdmin(isAdmin); }, [isAdmin, setIsAdmin]);

  // Feature gating (platform admin → Features). When a flag is off, hide its
  // control; the backend also 403s the call so it can't be reached anyway.
  const explainEnabled = useFeature('ai_explain');
  const askEnabled = useFeature('ai_ask');
  // BP 22 — the Class button only appears while a class session is live for
  // THIS lesson (features/classroom's shared 5 s poll of /class/live).
  const classEnabled = useClassroomStore((s) => s.liveForLesson(lessonId));

  const [busy, setBusy] = useState(true);
  // Desktop (lg+) inline rail visibility. Unchanged from before.
  const [railOpen, setRailOpen] = useState(true);
  // Focus mode (AppShell) hides the app chrome; the slide rail is chrome too,
  // so it goes with it. Read rather than written here: the user's own railOpen
  // preference is left untouched, so leaving focus mode restores the rail
  // exactly as they had it instead of silently resetting it to open.
  const focusMode = useUiStore((s) => s.focusMode);
  // The AI panel at double width. Explain and Tutor answers are long — worked
  // examples carry tables and multi-step working — and 24rem forces them into a
  // column barely wide enough for a sentence. Instructor: "have an option to
  // expnd the right panel for AI related tab to expand by x2". Per-visit, not
  // persisted: it trades away the slide, so it should be a choice the reader
  // makes for the answer in front of them rather than a setting they forget is
  // on and then wonder why the stage is narrow.
  const [panelWide, setPanelWide] = useState(false);
  // <lg only: the slide list is a left off-canvas drawer (the inline rail is
  // hidden below lg). Kept separate from `railOpen` so the desktop preference is
  // never clobbered by phone interactions.
  const [mobileRailOpen, setMobileRailOpen] = useState(false);
  // Wraps the stage + transport; this is what enters fullscreen so the audio
  // play/seek controls stay visible there.
  const playerSurfaceRef = useRef<HTMLDivElement>(null);
  // The scrolling slide body, which doubles as the swipe surface.
  const slideScrollRef = useRef<HTMLDivElement>(null);
  // Resume point from /progress: seek `seconds` only while we're on the slide it
  // belongs to. Deriving the per-slide value (vs. a separate clearing effect)
  // avoids a race where the initial goToSlide wipes the resume position before
  // the audio element seeks. Once the learner navigates away it stops matching.
  const [resumeTarget, setResumeTarget] = useState<{ index: number; seconds: number } | null>(null);
  // Guards POST /progress/complete to at most one call per mount — the
  // backend itself is idempotent (awards XP once, ever), this just avoids
  // firing it repeatedly as later signals keep satisfying "fully engaged".
  const completedFiredRef = useRef(false);
  // Slide ids credited via the dwell (reading) fallback — see
  // lessonCompletion.ts. Never written to the store: it must not affect
  // SlideProgressRail's advance-when-covered gating, only this component's
  // own completion decision.
  const dwellCoveredRef = useRef<Set<string>>(new Set());
  // Real, server-confirmed XP total across the whole course (BP 12
  // /progress/summary), shown in the header badge.
  const [totalXp, setTotalXp] = useState<number | null>(null);

  useShellTitle(lesson?.title || 'Lecture');

  // Swipe left/right to move between slides on touch devices. It calls the same
  // store actions as the transport buttons, so a swipe and a tap are
  // indistinguishable downstream — progress, audio and XP all behave identically.
  // The hook is intentionally strict about horizontal-vs-vertical intent: this
  // element also scrolls, and a gesture that hijacks ambiguous drags would make
  // reading a long slide miserable.
  const swipeNext = useLectureStore((st) => st.nextSlide);
  const swipePrev = useLectureStore((st) => st.prevSlide);
  useSwipeNavigation(slideScrollRef, { onNext: swipeNext, onPrev: swipePrev });

  // Tapping a slide inside the mobile drawer calls the rail's goToSlide, which
  // moves currentSlideIndex — closing the drawer on that change keeps the
  // "tap a slide → drawer closes" contract without touching SlideProgressRail.
  useEffect(() => {
    setMobileRailOpen(false);
  }, [currentSlideIndex]);

  // ---- Load lesson, audio manifest, and resume point ------------------------
  useEffect(() => {
    let cancelled = false;
    resetForLesson(lessonId);
    setBusy(true);
    completedFiredRef.current = false;
    dwellCoveredRef.current = new Set();

    (async () => {
      try {
        const [lessonRes, audioRes, progress, summary] = await Promise.all([
          getLesson(lessonId),
          getLessonAudio(lessonId).catch(() => ({ slides: [] })),
          getProgress(lessonId),
          getProgressSummary().catch(() => null),
        ]);
        if (cancelled) return;

        loadLesson(lessonId, lessonRes.lesson, lessonRes.slides, audioRes.slides);
        setTotalXp(summary?.totals.xp ?? 0);

        // Restore last slide + audio position.
        if (progress) {
          const idx = Math.max(
            0,
            Math.min(progress.slide_index ?? 0, lessonRes.slides.length - 1)
          );
          goToSlide(idx);
          if (progress.audio_position_ms > 0) {
            setResumeTarget({ index: idx, seconds: progress.audio_position_ms / 1000 });
          }
          // Already completed server-side (e.g. reopening a finished lesson):
          // don't re-fire completion just because every slide reads as
          // "engaged" again this visit. The backend would no-op it anyway
          // (awarded_xp: 0), but there's no reason to make the call.
          if (progress.status === 'completed') completedFiredRef.current = true;
        }
      } catch {
        if (!cancelled) setLoadError('Could not load this lesson.');
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId]);

  // ---- Persist progress (throttled at the AudioController; this is the sink) -
  const persistPosition = useCallback(
    (slideIndex: number, positionMs: number) => {
      putProgress({
        lesson_id: lessonId,
        slide_index: slideIndex,
        audio_position_ms: positionMs,
        status: 'in_progress',
      }).catch(() => {
        /* best-effort; the next tick retries */
      });
    },
    [lessonId]
  );

  // ---- Real-engagement lesson completion -------------------------------
  // See lessonCompletion.ts for the full rationale. In short: a lesson is
  // "completed" once every slide has been covered (audio ended / widget
  // answered — the existing signal, unchanged) or dwelt on long enough to
  // plausibly have been read. This fires once per mount (completedFiredRef)
  // on top of the backend's own once-ever award.
  const evaluateCompletion = useCallback(() => {
    if (completedFiredRef.current) return;
    const slideIds = slides.map((s) => s.id);
    if (!isLessonFullyEngaged(slideIds, completedSlides, dwellCoveredRef.current)) return;
    completedFiredRef.current = true;
    completeLesson(lessonId)
      .then((res) => {
        if (res.awarded_xp) setTotalXp((x) => (x ?? 0) + res.awarded_xp);
      })
      .catch(() => {
        // Let a later signal (or the next mount) retry.
        completedFiredRef.current = false;
      });
  }, [slides, completedSlides, lessonId]);

  // Re-check whenever the store's covered set changes — this is what fires
  // completion for a student who listens (handleAudioEnded → markComplete)
  // or answers a gating widget (handleWidgetComplete → markSlideCovered)
  // without either handler needing to know about completion itself.
  useEffect(() => {
    evaluateCompletion();
  }, [evaluateCompletion]);

  // Latest evaluateCompletion, read by the dwell timer below without being a
  // dependency of it — so a slide covered elsewhere (which changes
  // evaluateCompletion's identity) never resets an unrelated slide's
  // in-progress dwell wait back to zero.
  const evaluateCompletionRef = useRef(evaluateCompletion);
  useEffect(() => {
    evaluateCompletionRef.current = evaluateCompletion;
  }, [evaluateCompletion]);

  // The dwell (reading) fallback: once the current slide has been on screen
  // for MIN_SLIDE_DWELL_MS, credit it even with no audio-ended/widget event.
  // Cleared if the learner moves on first, so a rapid click-through earns no
  // credit for slides it never really sat on.
  useEffect(() => {
    const slide = slides[currentSlideIndex];
    if (!slide || dwellCoveredRef.current.has(slide.id)) return;
    const timer = window.setTimeout(() => {
      dwellCoveredRef.current.add(slide.id);
      evaluateCompletionRef.current();
    }, MIN_SLIDE_DWELL_MS);
    return () => window.clearTimeout(timer);
  }, [currentSlideIndex, slides]);

  // ---- Audio ended: cover the slide (existing signal; unchanged) --------
  const handleAudioEnded = useCallback(
    (slideIndex: number) => {
      const slide = slides[slideIndex];
      if (slide) markComplete(slide.id);
    },
    [slides, markComplete]
  );

  // ---- Widget complete: cover the slide (satisfies on_widget gating) ---------
  const handleWidgetComplete = useCallback(
    (_widget: WidgetRef) => {
      const slide = slides[currentSlideIndex];
      if (slide) markSlideCovered(slide.id);
    },
    [slides, currentSlideIndex, markSlideCovered]
  );

  // Resume seek only applies on the exact slide it was captured for; any other
  // slide (i.e. after the learner navigates) starts from 0.
  const resumeSeconds =
    resumeTarget && resumeTarget.index === currentSlideIndex
      ? resumeTarget.seconds
      : undefined;

  // ---- Render states --------------------------------------------------------
  if (busy && !isLoaded) {
    return (
      <div className="grid h-full place-items-center">
        <Spinner size={22} label="Loading lecture…" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="grid h-full place-items-center p-10 text-center">
        <div className="max-w-sm">
          <p className="text-sm font-medium text-ws-ink">{loadError}</p>
          <p className="mt-1 text-xs text-ws-ink-3">
            Try reloading the page, or pick another lesson.
          </p>
        </div>
      </div>
    );
  }

  if (slides.length === 0) {
    return (
      <div className="grid h-full place-items-center p-10 text-center">
        <div className="max-w-sm">
          <p className="text-sm font-medium text-ws-ink">This lesson has no slides yet</p>
          <p className="mt-1 text-xs text-ws-ink-3">Check back once content is published.</p>
        </div>
      </div>
    );
  }

  const allCovered =
    slides.length > 0 && slides.every((s) => completedSlides.includes(s.id));

  // Overall lesson progress (covered slides ÷ total) feeds the header rail.
  const coveredCount = slides.filter((s) => completedSlides.includes(s.id)).length;
  const progressPct = slides.length > 0 ? (coveredCount / slides.length) * 100 : 0;

  return (
    <div className="flex h-full flex-col">
      {/* Header strip: title, progress, XP, panel toggles */}
      <div className="flex items-center gap-3 border-b border-ws-border bg-ws-surface px-4 py-2">
        {/* <lg: opens the slide list as a left drawer. Hidden on lg+ where the
            inline rail (and its own toggle below) takes over. ≥40px tap target. */}
        <button
          onClick={() => setMobileRailOpen(true)}
          className="grid h-10 w-10 shrink-0 place-items-center rounded-md text-ws-ink-3 transition-colors hover:bg-ws-surface-2 lg:hidden"
          aria-label="Show slide list"
          title="Show slide list"
        >
          <ListOrdered size={18} />
        </button>

        {/* lg+: toggles the inline desktop rail. Unchanged behavior. */}
        <button
          onClick={() => setRailOpen((o) => !o)}
          className="hidden h-8 w-8 shrink-0 place-items-center rounded-md text-ws-ink-3 transition-colors hover:bg-ws-surface-2 lg:grid"
          title={railOpen ? 'Hide slide list' : 'Show slide list'}
        >
          {railOpen ? <PanelLeftClose size={16} /> : <PanelLeft size={16} />}
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <h1 className="truncate text-sm font-semibold text-ws-ink">
              {lesson?.title || 'Lecture'}
            </h1>
            {/* Download-for-offline + the "Available offline" badge. It sits with
                the title because it describes THIS lesson, and the badge is the
                thing a learner checks before they lose signal. */}
            {lesson?.id && <OfflineLessonButton lessonId={lesson.id} compact />}
          </div>
          <div className="flex items-center gap-2">
            {/* <lg: the slide counter doubles as a button to open the slide
                drawer. lg+: a plain label (the inline rail handles browsing). */}
            <button
              type="button"
              onClick={() => setMobileRailOpen(true)}
              className="shrink-0 rounded text-[11px] text-ws-ink-3 underline-offset-2 hover:text-ws-ink-2 hover:underline lg:pointer-events-none lg:no-underline lg:hover:text-ws-ink-3"
              aria-label="Open slide list"
            >
              Slide {currentSlideIndex + 1} of {slides.length}
            </button>
            {/* Slim overall-progress rail: fills as slides are covered. */}
            <div
              className="h-1 max-w-[160px] flex-1 overflow-hidden rounded-full bg-ws-surface-2"
              role="progressbar"
              aria-valuenow={Math.round(progressPct)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Lesson progress"
            >
              <div
                className="h-full rounded-full bg-ws-accent transition-[width] duration-500 ease-[var(--ws-ease)]"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            {allCovered && (
              <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-ws-success">
                <CheckCircle2 size={11} /> Complete
              </span>
            )}
          </div>
        </div>

        <span
          className="inline-flex shrink-0 items-center gap-1 rounded-md bg-ws-accent-quiet px-2 py-1 text-xs font-semibold text-ws-accent-ink"
          title="Total XP earned across all lessons"
        >
          <Zap size={12} />
          {totalXp ?? 0} XP
        </span>

        <div className="flex shrink-0 items-center gap-1">
          {isAdmin && (
            <button
              onClick={() => setNotesVisible(!notesVisible)}
              aria-pressed={notesVisible}
              className={`inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition-colors ${
                notesVisible
                  ? 'border-ws-accent bg-ws-accent text-ws-on-accent'
                  : 'border-ws-border bg-ws-surface text-ws-ink-2 hover:bg-ws-surface-2'
              }`}
              title={notesVisible ? 'Notes on — transcript visible to you. Click to hide.' : 'Notes off — transcript hidden. Click to show (instructor only).'}
            >
              <NotebookText size={14} />
              <span className="hidden sm:inline">Notes {notesVisible ? 'on' : 'off'}</span>
            </button>
          )}
          {/* The Explain button used to sit here, opening a second AI panel
              whose four chips — Explain, Go deeper, Simpler, Worked example —
              were the same four the Tutor panel already offered, served from
              the SAME cache (LectureRagService.tutor short-circuits a chip
              pressed with no typed message into ExplanationCacheService). Two
              buttons, two panels, one set of answers. Tutor is the superset:
              it adds Check my understanding and a follow-up box, and Read
              aloud moved across with the merge, so nothing was lost by
              removing this one. */}
          {askEnabled && (
            <button
              onClick={() => setPanel(panel === 'ask' ? 'none' : 'ask')}
              aria-pressed={panel === 'ask'}
              className={`inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition-colors ${
                panel === 'ask'
                  ? 'border-ws-ai bg-ws-ai-pale text-ws-ai-deep'
                  : 'border-ws-border bg-ws-surface text-ws-ink-2 hover:bg-ws-surface-2'
              }`}
              title="Ask a question about this lesson"
            >
              <MessageCircleQuestion size={14} />
              <span className="hidden sm:inline">Ask</span>
            </button>
          )}
          {explainEnabled && (
            <button
              onClick={() => setPanel(panel === 'tutor' ? 'none' : 'tutor')}
              aria-pressed={panel === 'tutor'}
              className={`inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition-colors ${
                panel === 'tutor'
                  ? 'border-ws-ai bg-ws-ai-pale text-ws-ai-deep'
                  : 'border-ws-border bg-ws-surface text-ws-ink-2 hover:bg-ws-surface-2'
              }`}
              title="AI Tutor — explain, go deeper, worked examples, check your understanding"
            >
              <GraduationCap size={14} />
              <span className="hidden sm:inline">Tutor</span>
            </button>
          )}
          {classEnabled && (
            <button
              onClick={() => setPanel(panel === 'class' ? 'none' : 'class')}
              aria-pressed={panel === 'class'}
              className={`inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition-colors ${
                panel === 'class'
                  ? 'border-ws-accent bg-ws-accent-quiet text-ws-accent-ink'
                  : 'border-ws-border bg-ws-surface text-ws-ink-2 hover:bg-ws-surface-2'
              }`}
              title="Class — this lesson's live activity"
            >
              <Users size={14} />
              <span className="hidden sm:inline">Class</span>
            </button>
          )}
        </div>
      </div>

      {/* Body: rail | stage+transport | side panel */}
      <div className="relative flex min-h-0 flex-1">
        {/* Inline desktop rail — lg+ only (drawer covers everything below lg). */}
        {railOpen && !focusMode && (
          <aside className="hidden w-[15.4rem] shrink-0 border-r border-ws-border bg-ws-rail lg:block">
            <SlideProgressRail />
          </aside>
        )}

        {/* <lg: slide list as a left off-canvas drawer over the player. */}
        {mobileRailOpen && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <div
              className="ws-fade-in absolute inset-0 bg-ws-overlay"
              onClick={() => setMobileRailOpen(false)}
              aria-hidden
            />
            <div className="ws-slide-in-left absolute inset-y-0 left-0 flex w-[85%] max-w-xs flex-col border-r border-ws-border bg-ws-rail shadow-lg">
              <div className="flex items-center justify-between border-b border-ws-border px-3 py-2">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-ws-ink-3">
                  Slides
                </span>
                <button
                  onClick={() => setMobileRailOpen(false)}
                  className="grid h-10 w-10 place-items-center rounded-md text-ws-ink-3 transition-colors hover:bg-ws-surface-2"
                  aria-label="Close slide list"
                  title="Close"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto">
                <SlideProgressRail />
              </div>
            </div>
          </div>
        )}

        <main className="flex min-w-0 flex-1 flex-col">
          {/* Surface that goes fullscreen: stage + transport together, so the
              play/seek controls stay on screen in fullscreen. */}
          <div ref={playerSurfaceRef} className="flex min-h-0 flex-1 flex-col bg-ws-bg">
            <div ref={slideScrollRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              <SlideStage
                slide={slides[currentSlideIndex]}
                onWidgetComplete={handleWidgetComplete}
                surfaceRef={playerSurfaceRef}
              />
            </div>
            <AudioController
              onPersistPosition={persistPosition}
              onAudioEnded={handleAudioEnded}
              resumeSeconds={resumeSeconds}
            />
          </div>
        </main>

        {/*
          TUTOR PANEL MOUNT POINT (right rail) — the team lead mounts an AI
          TutorPanel into this slot later. Render it as a sibling <aside> here
          (e.g. when a `panel === 'tutor'` mode is active) so it shares the
          existing right-rail layout with Explain / Ask. Do not import it from
          this agent's change set.
        */}
        {/* 'explain' is kept as an alias of 'tutor' rather than removed: the
            panel is persisted and deep-linkable, so a value stored before the
            two panels merged must still open something rather than nothing. */}
        {(((panel === 'tutor' || panel === 'explain') && explainEnabled) ||
          (panel === 'ask' && askEnabled) ||
          (panel === 'class' && classEnabled)) && (
          <>
            {/* <lg: tinted scrim behind the full-screen overlay. lg+: none. */}
            <div
              className="ws-fade-in fixed inset-0 z-30 bg-ws-overlay lg:hidden"
              onClick={() => setPanel('none')}
              aria-hidden
            />
            {/*
              <lg: full-screen overlay over the player so the stage is never
              crushed. lg+: the original inline side aside (lg:w-96), unchanged.
            */}
            <aside
              className={`fixed inset-0 z-40 flex min-h-0 flex-col bg-ws-surface lg:static lg:inset-auto lg:z-auto lg:shrink-0 lg:border-l lg:border-ws-border lg:bg-transparent ${
                panelWide ? 'lg:w-[48rem]' : 'lg:w-96'
              }`}
            >
              {/* The width control used to be a strip of its own here — one
                  button on the far right of an otherwise empty row, the only
                  control on screen not grouped with its siblings. It moved into
                  each panel's header, beside Clear and Close, which is where
                  the other things-you-do-to-the-panel already are. */}
              {/* <lg: a header row with a close control. Hidden on lg+ where the
                  panels supply their own in-pane close. The panel below keeps its
                  own h-full chat layout (scrolls internally, composer pinned). */}
              <div className="flex shrink-0 items-center justify-end border-b border-ws-border px-3 py-2 lg:hidden">
                <button
                  onClick={() => setPanel('none')}
                  className="inline-flex h-10 items-center gap-1.5 rounded-md px-3 text-sm font-medium text-ws-ink-2 transition-colors hover:bg-ws-surface-2"
                  aria-label="Close panel"
                  title="Close"
                >
                  <X size={18} /> Close
                </button>
              </div>
              <div className="min-h-0 flex-1">
                {panel === 'ask' ? (
                  <AskQuestionPanel
                    lessonId={lessonId}
                    onClose={() => setPanel('none')}
                    wide={panelWide}
                    onToggleWide={() => setPanelWide((v) => !v)}
                  />
                ) : panel === 'class' ? (
                  <ClassPanel
                    lessonId={lessonId}
                    onClose={() => setPanel('none')}
                    wide={panelWide}
                    onToggleWide={() => setPanelWide((v) => !v)}
                  />
                ) : (
                  <TutorPanel
                    lessonId={lessonId}
                    slideIndex={currentSlideIndex}
                    slideTitle={slides[currentSlideIndex]?.heading ?? undefined}
                    onClose={() => setPanel('none')}
                    wide={panelWide}
                    onToggleWide={() => setPanelWide((v) => !v)}
                  />
                )}
              </div>
            </aside>
          </>
        )}
      </div>
    </div>
  );
}
